/**
 * The registration spreadsheet — ONE definition, used twice.
 *
 * The admin Submissions screen has always built its "Export CSV" in the
 * browser, from the rows already in hand. The live Google Sheets feed
 * (`GET /api/feed/enrolments.csv`) has to build the same spreadsheet in the
 * Worker, from the database. Two implementations of "what are the columns"
 * would drift the first time a host added a question — the download and the
 * linked sheet would disagree about the same registrations, and there would be
 * no way to tell which was right.
 *
 * So the column logic lives here, in the package both sides already depend on,
 * and is deliberately PURE: rows in, strings out, no fetch, no DOM, no D1.
 *
 * See `enrolmentAnswerColumns` for why the columns come from the form
 * definitions rather than from the answers.
 */
import type { Enrolment, FormField } from "./index";
import { NO_SHIRT_ANSWER } from "./index";
import { splitPersonName } from "./person-name";
import { formatPhone } from "./phone";

/** The program facts a spreadsheet row needs: its display name, and which
 *  question set it asks. Structural on purpose — the Worker passes raw D1 rows
 *  and the admin screen passes parsed `Program`s. */
export interface EnrolmentCsvProgram {
  id: number | string;
  title: string;
  form_key?: string | null;
}

/** One exported answer column: the heading a team reads, and the key it reads
 *  from. `type` is the question's input type where a live form still asks it —
 *  carried so a phone answer can be written the way a spreadsheet keeps its
 *  leading zero. Absent for a retired question nothing asks any more. */
export interface EnrolmentCsvColumn {
  header: string;
  name: string;
  type?: FormField["type"];
}

/**
 * The always-present columns, in order. Answer columns follow these.
 *
 * The *_first_name / *_last_name pairs sit beside the name they come from and
 * are DERIVED (see `splitPersonName`), not stored — the form still asks for a
 * name in one box. They are here so a sheet can be sorted or filtered by
 * surname, which the single name column cannot do. The full name stays, in its
 * original position, because it is what everyone reads and because a formula
 * already pointing at it must keep working.
 */
export const ENROLMENT_CSV_FIXED_COLUMNS = [
  "id",
  "created_at",
  "parent_name",
  "parent_first_name",
  "parent_last_name",
  "parent_email",
  "parent_phone",
  "child_id",
  "child_name",
  "child_first_name",
  "child_last_name",
  "registrant_kind",
  "year_level",
  "program_id",
  "program",
  "payment_ref",
  "amount_aud",
  "payment_method",
  "payment_status",
  "status",
  "paid_at",
] as const;

/** Answers the journey promotes to their own top-level column already. */
const KNOWN_COLUMN_FIELDS = new Set([
  "parent_name",
  "parent_email",
  "parent_phone",
  "child_name",
  "year_level",
  "program_id",
]);

/** Journey/system keys that get friendly labels; everything else is a
 *  host-added form field and is title-cased from its snake_case name. */
const EXTRA_LABELS: Record<string, string> = {
  cash_when: "Cash handover",
  already_note: "How they paid",
};

/** Internal keys never shown to the host (they aren't submission content). */
const EXTRA_HIDDEN = new Set(["turnstileToken"]);

function humanizeKey(key: string): string {
  /* Read at CALL time, not as a computed key in EXTRA_LABELS above: this
     module and index.ts import from each other, and a constant read while the
     module body is still evaluating can legitimately be undefined. */
  if (key === NO_SHIRT_ANSWER) return "Shirt not needed";
  if (EXTRA_LABELS[key]) return EXTRA_LABELS[key];
  return key
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/**
 * Work out one CSV column per question, in the order the forms ask them.
 *
 * Three things make this more than a key scan:
 *
 *  - The rows can span SEVERAL programs, and each program has its own question
 *    set (`form_key`). The column list is the union across the forms actually
 *    present, so a mixed export still lines up.
 *  - A question a family skipped is simply ABSENT from `extra`, so the columns
 *    cannot be discovered from the answers alone — they come from the form
 *    definitions, which is also what puts them in the order the parent saw.
 *  - Retired questions still have answers on old registrations. Those are
 *    appended at the end rather than dropped, so an export never quietly loses
 *    something a family told us.
 *
 * Headers are the question's LABEL ("Dietary requirements"), not its internal
 * name, because the person receiving the spreadsheet has never seen the
 * internal name. Where two different questions share a label, the name is
 * appended to keep the columns distinct.
 */
export function enrolmentAnswerColumns(
  rows: Enrolment[],
  formFields: FormField[],
  programs: EnrolmentCsvProgram[],
): EnrolmentCsvColumn[] {
  const byId = new Map(programs.map((p) => [String(p.id), p]));
  const formKeyFor = (programId: string | null | undefined): string =>
    (programId ? byId.get(String(programId))?.form_key : null) ?? "enrolment";

  // Which question sets are in play, in the order the rows introduce them.
  const formKeys: string[] = [];
  for (const e of rows) {
    const key = formKeyFor(e.program_id);
    if (!formKeys.includes(key)) formKeys.push(key);
  }

  const columns: EnrolmentCsvColumn[] = [];
  const seen = new Set<string>();
  const add = (name: string, label: string, type?: FormField["type"]) => {
    if (seen.has(name)) return;
    seen.add(name);
    columns.push({ header: label, name, type });
  };

  for (const key of formKeys) {
    const fields = formFields
      .filter((f) => f.form_key === key && f.archived === 0)
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    for (const f of fields) {
      // The journey promotes these to their own columns already.
      if (KNOWN_COLUMN_FIELDS.has(f.name)) continue;
      add(f.name, f.label, f.type);
    }
  }

  // Anything answered that no live form asks for any more.
  for (const e of rows) {
    for (const name of Object.keys(e.extra ?? {})) {
      if (EXTRA_HIDDEN.has(name) || KNOWN_COLUMN_FIELDS.has(name)) continue;
      add(name, humanizeKey(name));
    }
  }

  /*
   * Make every header unique, and never equal to one of the fixed columns.
   *
   * Cells are looked up BY HEADER, so two columns sharing a header silently
   * become one. A host is free to label a question "Status" or "Program" —
   * which would otherwise overwrite the real payment status in the export,
   * quietly and with no error anywhere.
   */
  const taken = new Set<string>(ENROLMENT_CSV_FIXED_COLUMNS);
  const labelCounts = new Map<string, number>();
  for (const c of columns) labelCounts.set(c.header, (labelCounts.get(c.header) ?? 0) + 1);

  return columns.map((c) => {
    let header = (labelCounts.get(c.header) ?? 0) > 1 ? `${c.header} (${c.name})` : c.header;
    if (taken.has(header)) header = `${header} (${c.name})`;
    while (taken.has(header)) header = `${header} `;
    taken.add(header);
    return { header, name: c.name, type: c.type };
  });
}

/** One answer as a spreadsheet cell. Ticks read Yes/No, not true/false. */
export function enrolmentCsvAnswer(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** The whole spreadsheet as headers + keyed rows, before it becomes text. */
export function enrolmentCsvTable(
  rows: Enrolment[],
  formFields: FormField[],
  programs: EnrolmentCsvProgram[],
): { columns: string[]; rows: Array<Record<string, string | number>> } {
  const byId = new Map(programs.map((p) => [String(p.id), p]));
  const programName = (id: string | null | undefined): string =>
    id ? (byId.get(String(id))?.title ?? `Program #${id}`) : "";

  const answers = enrolmentAnswerColumns(rows, formFields, programs);

  return {
    columns: [...ENROLMENT_CSV_FIXED_COLUMNS, ...answers.map((a) => a.header)],
    rows: rows.map((e) => {
      const extra = e.extra ?? {};
      const parent = splitPersonName(e.parent_name);
      const child = splitPersonName(e.child_name);
      const row: Record<string, string | number> = {
        id: e.id,
        created_at: e.created_at,
        parent_name: e.parent_name,
        parent_first_name: parent.first,
        parent_last_name: parent.last,
        parent_email: e.parent_email,
        // Grouped, so the spreadsheet keeps it as text and the leading 0 of a
        // mobile survives the import. See `formatPhone`.
        parent_phone: formatPhone(e.parent_phone),
        child_id: e.child_id ?? "",
        child_name: e.child_name,
        child_first_name: child.first,
        child_last_name: child.last,
        registrant_kind: e.registrant_kind,
        year_level: e.year_level,
        program_id: e.program_id ?? "",
        program: programName(e.program_id),
        payment_ref: e.payment_ref,
        amount_aud: (e.amount_cents / 100).toFixed(2),
        payment_method: e.payment_method ?? "",
        payment_status: e.payment_status,
        status: e.status,
        paid_at: e.paid_at ?? "",
      };
      for (const a of answers) {
        const cell = enrolmentCsvAnswer(extra[a.name]);
        /* An emergency contact's mobile is as much a phone number as the
           parent's, and loses its leading zero the same way — so every
           question the form asks as a PHONE gets the same treatment, not just
           the one column that happens to be built in. */
        row[a.header] = a.type === "tel" ? formatPhone(cell) : cell;
      }
      return row;
    }),
  };
}

/**
 * Serialise headers + keyed rows as CSV text.
 *
 * `bom` prefixes U+FEFF, which desktop Excel needs to read the file as UTF-8 —
 * without it an accented name in a downloaded file arrives mangled. It is OFF
 * by default because a URL-based feed must NOT have it: Google Sheets treats
 * the BOM as data and the first heading arrives with an invisible character
 * stuck to the front of it, which then breaks every formula that references
 * that column by name.
 */
export function toCsv(
  columns: string[],
  rows: Array<Record<string, string | number>>,
  opts: { bom?: boolean } = {},
): string {
  const escape = (value: string | number): string => {
    const s = String(value);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map(escape).join(",");
  const body = rows.map((row) => columns.map((col) => escape(row[col] ?? "")).join(",")).join("\n");
  return `${opts.bom ? "﻿" : ""}${header}\n${body}`;
}

/** The registration spreadsheet as CSV text, end to end. */
export function buildEnrolmentCsv(
  rows: Enrolment[],
  formFields: FormField[],
  programs: EnrolmentCsvProgram[],
  opts: { bom?: boolean } = {},
): string {
  const { columns, rows: cells } = enrolmentCsvTable(rows, formFields, programs);
  return toCsv(columns, cells, opts);
}
